import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme as UITheme } from '../../theme';
import rem from '../../utils/rem';
import { Icon } from '../icon';
import { Box, BoxProps, Flex, Text } from '../primitives';

type Props = Omit<React.HTMLProps<HTMLInputElement>, 'value'> & {
  label?: string;
  error?: boolean;
  containerProps?: Omit<BoxProps, 'color'>;
  searchResults?: {
    id: string;
    label: string;
    postcode?: string;
    country?: string;
  }[];
  search: (value: string) => void;
  selectedValue?: string;
  onValueSelected: (id?: string) => void;
};

const TextInputAutocomplete = ({
  label,
  error,
  containerProps,
  searchResults = [],
  search,
  selectedValue,
  onValueSelected,
  ...rest
}: Props) => {
  const [resultsDisplayed, setResultsDisplayed] = useState(false);
  const [value, setValue] = useState(selectedValue);

  const input = useRef<HTMLInputElement>();

  useEffect(() => {
    setValue(selectedValue);
  }, [selectedValue]);

  const debounceSearch = debounce(search, 300);

  const handleDeleteClick = () => {
    setResultsDisplayed(false);
    onValueSelected();
  };

  const handleItemClicked = (id: string) => () => {
    setResultsDisplayed(false);
    onValueSelected(id);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);

    if (e.target.value.length) {
      setResultsDisplayed(true);
      debounceSearch(e.target.value);
    } else setResultsDisplayed(false);
  };

  const handleLabelClick = useCallback(() => {
    if (input && input.current) input.current.focus();
  }, [input]);

  return (
    <SContainer flexDirection="column" {...containerProps}>
      {label && (
        <Text
          fontSize={rem(20)}
          lineHeight={rem(27)}
          fontWeight={600}
          mb="medium"
          onClick={handleLabelClick}
        >
          {label}
        </Text>
      )}
      <Flex
        alignItems="center"
        bg={selectedValue ? UITheme.colors.pippin : UITheme.colors.alabaster}
        borderRadius={rem(16)}
        borderBottomLeftRadius={resultsDisplayed ? 0 : rem(16)}
        borderBottomRightRadius={resultsDisplayed ? 0 : rem(16)}
        height={rem(32)}
        px="medium"
      >
        <SInput
          as={'input' as any}
          ref={input}
          error={error}
          border={0}
          disabled={!!selectedValue}
          backgroundColor="transparent_white"
          outline="none"
          fontSize={rem(16)}
          lineHeight={rem(22)}
          p="0"
          pr="medium"
          color={error || !!selectedValue ? 'accent' : 'primary'}
          fontWeight={selectedValue ? 600 : 'normal'}
          onChange={onChange}
          {...rest}
          value={value}
        />
        {!!selectedValue && (
          <Flex
            alignItems="center"
            onClick={handleDeleteClick}
            cursor="pointer"
            flexGrow={0}
          >
            <Icon name="cross" color="accent" size={rem(16)} />
          </Flex>
        )}

        {resultsDisplayed && (
          <SResultList py="small" overflow="hidden">
            {searchResults.map(item => (
              <SResultListItem
                px="medium"
                py="small"
                key={item.id}
                cursor="pointer"
                onClick={handleItemClicked(item.id)}
              >
                {item.label}
                <SSpan>{`${
                  item.postcode === null ? '' : ', ' + item.postcode
                }, ${item.country}`}</SSpan>
              </SResultListItem>
            ))}
            <SCopyright>{`© OpenStreetMap Contributors`}</SCopyright>
          </SResultList>
        )}
      </Flex>
    </SContainer>
  );
};

const SContainer = styled(Flex)`
  transition: border-color 200ms ease;
`;

const SInput = styled(Box)<{ error?: boolean }>`
  transition: color 200ms ease;
  flex: 1;

  &::placeholder {
    color: ${({ theme, error }) =>
      error ? theme.colors.accent : theme.colors.secondary};
    font-style: italic;
    font-size: ${rem(16)};
    line-height: ${rem(22)};
    transition: color 200ms ease;
  }
`;

const SResultList = styled(Box)`
  position: absolute;
  z-index: 1;
  left: 0;
  right: 0;
  top: 100%;
  padding-bottom: ${({ theme }) => theme.space.xLarge};
  background-color: ${({ theme }) => theme.colors.alabaster};
  border-bottom-left-radius: ${rem(16)};
  border-bottom-right-radius: ${rem(16)};
`;

const SResultListItem = styled(Box)`
  color: ${({ theme }) => theme.colors.primary};
  &:hover {
    background-color: ${({ theme }) => theme.colors.accentHover};
  }
`;

const SSpan = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
`;

const SCopyright = styled.span`
  position: absolute;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.size12};
  right: ${rem(10)};
  bottom: ${({ theme }) => theme.space.small};
`;

export default TextInputAutocomplete;
